import * as React from 'react';
import { IPatientUnlisted, ISearchPatientsUnlisted } from "../../../models/IPatientUnlisted"
import { PatientsUnlistedTable } from './PatientsUnlistedTable';
import { useAuth } from '../../../App';
import { AdditionalAssignmentApi } from "../../../api/AdditionalAssignmentApi";
import { PaginationNavigation, CustomSubmitButton, CustomSubmitButtonType, Form, SpinnerWrapper, AlertMessage, AlertType, SortTableContext } from "soprasteria-ui-library";
import { IPagination } from "soprasteria-ui-library/dist/models/IPagination";
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IPaginatedResponse } from '../../../api/ApiClient';
import { isInRole, UserContext } from "../../../contexts/UserContext";
import { LIBRETTOADMIN, VÅRDANSVARIG } from '../../../utils/RoleConstants';
import { setMessage } from '../../../helpers/MessageHelper';
import { PatientUnlistedUmbrellaSearch } from './PatientUnlistedUmbrellaSearch';
import { formatDate } from '../../../helpers/DateHelper';
import { UNLISTED_PATIENTS } from '../../../utils/RouteConstants';
import { useHistory } from 'react-router';

export const PatientsUnlisted: React.FunctionComponent<{}> = () => {
    const auth = useAuth();
    const api = new AdditionalAssignmentApi(auth);
    const userInfo = React.useContext(UserContext);

    const [tableData, setTableData] = React.useState<IPatientUnlisted[]>();
    const [isFetchingUnlistedPatients, setIsFetchingUnlistedPatients] = React.useState<boolean>(false);
    const [isExportingToExcel, setIsExportingToExcel] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>();

    const [paginationHeader, setPaginationHeader] = React.useState<IPagination>();
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles);
    const hasRoleSysAdmin = isInRole(LIBRETTOADMIN, userInfo.roles);
    const [searchParams, setSearchParams] = React.useState<ISearchPatientsUnlisted>({ pageNumber: 1, pageSize: 20 });
    const { sortOrder, selectedPropertyToSortBy, setSelectedPropertyToSortBy, setSortOrder } = React.useContext(SortTableContext);
    const history = useHistory();
    const pageSize = 20;
    const [isFirstSearch, setIsFirstSearch] = React.useState<boolean>(true);

    React.useEffect(() => {
        if (!hasRoleSysAdmin && !hasRoleParaply) {
            getUnlistedPatients(history.location.state !== undefined && history.location.state !== null ? history.location.state as any : { pageNumber: 1, pageSize: pageSize });
        }
    }, [])

    React.useEffect(() => {
        if (!isFirstSearch) {
            onSortOrderClick();
        }
    }, [selectedPropertyToSortBy, sortOrder])

    const getUnlistedPatients = (searchParams: ISearchPatientsUnlisted) => {
        setIsFetchingUnlistedPatients(true);
        if (setSelectedPropertyToSortBy && setSortOrder) {
            if (isFirstSearch) {
                setSelectedPropertyToSortBy(searchParams.propertyToOrderBy);
                if (searchParams?.sortOrder) {
                    setSortOrder(searchParams.sortOrder);
                }
            }
            else {
                setIsFirstSearch(true);
                setSelectedPropertyToSortBy(undefined);
            }
        }
        searchParams.pageSize = pageSize;
        searchParams.pageNumber = searchParams.pageNumber ?? 1;
        history.push(UNLISTED_PATIENTS, searchParams);

        api.getUnlistedPatients(searchParams, (response: IPaginatedResponse<IPatientUnlisted[]>) => {
            setTableData(response.data);
            setPaginationHeader(response.paginationHeader);
        }, (message) => {
            const newMessage = setMessage(message, errorMessage ?? "");
            setErrorMessage(newMessage);
            setTableData(undefined);
        }, () => {
            setIsFetchingUnlistedPatients(false);
        });

        setSearchParams(searchParams);
        setIsFirstSearch(false);
    }

    const handleClickedPage = (page: number) => {
        if (searchParams) {
            setIsFetchingUnlistedPatients(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = page;
            history.push(UNLISTED_PATIENTS, currentSearchParams);

            api.getUnlistedPatients(currentSearchParams, (response) => {
                setTableData(response.data);
                setPaginationHeader(response.paginationHeader);
            }, undefined, () => setIsFetchingUnlistedPatients(false));
        }
    }

    const onPatientsUnlistedToExcelSubmit = (params: ISearchPatientsUnlisted) => {
        setIsExportingToExcel(true);
        api.getUnlistedPatientsAsExcel(params,
            `Olistade patienter ${formatDate()}`,
            () => { },
            () => { },
            () => {
                setIsExportingToExcel(false);
            }
        );
    }

    const onSortOrderClick = () => {
        if (searchParams) {
            setIsFetchingUnlistedPatients(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = 1;
            currentSearchParams.sortOrder = sortOrder;
            currentSearchParams.propertyToOrderBy = selectedPropertyToSortBy;
            history.push(UNLISTED_PATIENTS, currentSearchParams);

            api.getUnlistedPatients(currentSearchParams, (response) => {
                setTableData(response.data);
                setPaginationHeader(response.paginationHeader);
            }, undefined, () => setIsFetchingUnlistedPatients(false));
        }
    }

    return (
        <SpinnerWrapper isLoading={isFetchingUnlistedPatients}>
            <section className="card">
                <div className="card-header">
                    <h2>Olistade patienter</h2>
                </div>
                <div className="card-body">
                    {
                        hasRoleParaply || hasRoleSysAdmin ?
                            <PatientUnlistedUmbrellaSearch searchPatientsUnlisted={getUnlistedPatients} exportToExcel={onPatientsUnlistedToExcelSubmit} isExportingToExcel={isExportingToExcel} preSetSearchParams={history.location.state !== undefined && history.location.state !== null ? history.location.state as any : undefined} />
                            : null
                    }
                    <AlertMessage duration={3000} alertType={AlertType.danger}>{errorMessage}</AlertMessage>
                    <section>
                        <div className="row">
                            {
                                !hasRoleParaply && !hasRoleSysAdmin ?
                                    <div className="col-8">
                                        <p>Här visas en sammanställning av olistade patienter som vårdgivarens mottagning enligt avtal har områdesansvar för.</p>
                                    </div>
                                    : null
                            }
                            <div className="col-4 text-right">
                                <Form>
                                    {
                                        !hasRoleParaply && !hasRoleSysAdmin ?
                                            <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative}
                                                title="Exportera till excel"
                                                onButtonSubmit={onPatientsUnlistedToExcelSubmit}
                                                isLoading={isExportingToExcel}><FontAwesomeIcon icon={faFileExcel} /> Exportera till Excel</CustomSubmitButton>
                                            : null
                                    }
                                </Form>
                            </div>
                        </div>
                        {
                            tableData ? <div>
                                <PatientsUnlistedTable id="patient-table" data={tableData} />
                                <PaginationNavigation
                                    ariaLabel="unlisted patients pagination selector"
                                    pagination={paginationHeader}
                                    pageNeighbours={2}
                                    selectedPage={page => handleClickedPage(page)}
                                    showCount={true} /> </div> :
                                null
                        }

                    </section>
                </div>
            </section>
        </SpinnerWrapper>
    )
}